import { useMemo, useRef } from 'react';
import { createLogger, Logger, LogContext } from '@/lib/structuredLogger';
import { useAuth } from '@/hooks/auth/use-auth';

/**
 * React hook for structured logging with automatic context
 *
 * @example
 * function MyComponent() {
 *   const logger = useLogger('MyComponent');
 *
 *   useEffect(() => {
 *     logger.info('Component mounted');
 *     return () => logger.debug('Component unmounting');
 *   }, [logger]);
 * }
 *
 * @param context - Component/hook name for log context
 * @param additionalContext - Additional context data.
 *   NOTE: Passing inline objects will cause re-renders. Use useMemo or stable references.
 */
export function useLogger(context: string, additionalContext?: LogContext): Logger {
  // Call useAuth unconditionally (React Hooks rules)
  // Will return undefined user if not in AuthProvider context
  const { user } = useAuth();
  const traceIdRef = useRef<string | undefined>(undefined);

  const logger = useMemo(() => {
    const contextData: LogContext = {
      traceId: traceIdRef.current,
      userId: user?.id,
      ...additionalContext,
    };

    const instance = createLogger(context, {}, contextData);

    // Store trace ID for consistency using public API
    if (!traceIdRef.current) {
      traceIdRef.current = instance.getTraceId();
    }

    return instance;
  }, [context, user?.id, additionalContext]);

  return logger;
}
